import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

// 创建一个唯一的标识符，包含产品ID和所有选项
const createItemKey = (item) => {
  const options = [];
  
  // 添加颜色选项
  if (item.color) options.push(`color-${item.color}`);
  if (item.selectedColor) options.push(`selectedColor-${item.selectedColor}`);
  
  // 添加其他选项
  if (item.connectivity) options.push(`connectivity-${item.connectivity}`);
  if (item.dpi) options.push(`dpi-${item.dpi}`);
  if (item.length) options.push(`length-${item.length}`);
  if (item.selectedLength) options.push(`selectedLength-${item.selectedLength}`);
  if (item.selectedSize) options.push(`selectedSize-${item.selectedSize}`);
  if (item.selectedPlug) options.push(`selectedPlug-${item.selectedPlug}`);
  if (item.version) options.push(`version-${item.version}`);
  
  // 添加Switch 2 Protect Screen特定选项
  if (item.selectedSurface) options.push(`surface-${item.selectedSurface.id}`);
  if (item.selectedAddOn) options.push(`addon-${item.selectedAddOn.id}`);
  
  // 添加其他add-on选项
  if (item.wristStrap) options.push('wristStrap');
  if (item.selectedEssentialOil) options.push(`essentialOil-${item.selectedEssentialOil}`);
  if (item.selectedAddOns) options.push(`addons-${item.selectedAddOns.join('-')}`);
  
  const optionsString = options.length > 0 ? `-${options.join('-')}` : '';
  return `${item.id}${optionsString}`;
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      
      const newItemKey = createItemKey(action.payload);
      const existingItem = state.items.find(item => createItemKey(item) === newItemKey);
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            createItemKey(item) === newItemKey
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_FROM_CART':
      // 移除特定配置的商品
      return {
        ...state,
        items: state.items.filter(item => {
          const itemKey = createItemKey(item);
          return itemKey !== action.payload;
        })
      };

    case 'UPDATE_QUANTITY':
      // 更新特定配置商品的数量
      return {
        ...state,
        items: state.items.map(item => {
          const itemKey = createItemKey(item);
          return itemKey === action.payload.itemKey
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item;
        }).filter(item => item.quantity > 0)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

const initialState = {
  items: []
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // 从localStorage加载购物车
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      dispatch({ type: 'LOAD_CART', payload: JSON.parse(savedCart) });
    }
  }, []);

  // 保存购物车到localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  const addToCart = (product, quantity = 1) => {
    dispatch({
      type: 'ADD_TO_CART',
      payload: { ...product, quantity }
    });
  };

  const removeFromCart = (itemKey) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: itemKey
    });
  };

  const updateQuantity = (itemKey, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { itemKey, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      // 处理价格格式 - 确保是字符串
      let priceStr = typeof item.price === 'string' ? item.price : item.price.toString();
      let itemTotal = parseFloat(priceStr.replace('S$', '')) * item.quantity;
      
      // 添加 add-on 价格
      if (item.selectedAddOns && Array.isArray(item.selectedAddOns) && item.selectedAddOns.length > 0) {
        item.selectedAddOns.forEach(addOn => {
          if (addOn && addOn.price && typeof addOn.price === 'string') {
            const addOnPrice = parseFloat(addOn.price.replace('S$', ''));
            if (!isNaN(addOnPrice)) {
              itemTotal += addOnPrice * item.quantity;
            }
          }
        });
      }
      
      return total + itemTotal;
    }, 0);
  };

  const getCartItemCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cart: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
